<template>
    <div class="pill-list">
        <h2 v-if="heading" class="pill-list__heading">{{ heading }}</h2>
        <div class="pill-list__wrapper" :class="wrapperModifierClasses">
            <div ref="pills" class="pill-list__pills" :class="pillListModifierClasses">
                <a v-for="(pill, index) in pills" :key="`pill-${index}`" class="pill" :href="pill.link">
                    {{ pill.title }}
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'PillList',
    props: {
        heading: {
            type: String,
            default: '',
        },
        pills: {
            type: Array,
            required: true,
        },
        noScroll: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            scrolledToEnd: false,
        };
    },
    computed: {
        ...mapGetters(['isLargeScreen']),
        wrapperModifierClasses() {
            return {
                'pill-list__wrapper--fade': !this.scrolledToEnd && !this.noScroll,
            };
        },
        pillListModifierClasses() {
            return {
                'pill-list__pills--no-scroll': this.noScroll,
            };
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.checkScrollPosition();
            this.$refs.pills.addEventListener('scroll', this.checkScrollPosition);
        });
    },
    beforeDestroy() {
        this.$refs.pills.removeEventListener('scroll', this.checkScrollPosition);
    },
    methods: {
        checkScrollPosition() {
            if (this.isLargeScreen || !this.$refs.pills) {
                return;
            }

            const pills = this.$refs.pills;
            const scrollWidth = pills.scrollWidth;
            const clientWidth = pills.clientWidth;
            const scrollLeft = pills.scrollLeft;

            // Not scrollable so we keep scrolledToEnd active
            if (scrollWidth <= clientWidth) {
                this.scrolledToEnd = true;

                return;
            }

            const tolerance = 5; // Allow for a 5-pixel discrepancy

            if (scrollWidth - clientWidth - scrollLeft <= tolerance) {
                this.scrolledToEnd = true;
            } else {
                this.scrolledToEnd = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.pill-list {
    overflow: hidden;

    &__wrapper {
        position: relative;

        &--fade {
            &:before {
                content: '';
                position: absolute;
                top: 0;
                right: -1px; // Fixes a 1px gap on the right side
                width: 100px;
                height: 100%;
                background: linear-gradient(270deg, $white 25%, transparent);
                pointer-events: none; /* Prevent the pseudo-element from blocking clicks */
                z-index: 2;
            }
        }
    }

    &__heading {
        @extend %heading-2;
        margin-bottom: 24px;

        @include lg {
            text-align: center;
            margin-bottom: 40px;
        }
    }

    &__pills {
        display: flex;
        gap: 24px;
        overflow-x: scroll;

        &::-webkit-scrollbar {
            display: none;
        }

        &--no-scroll {
            overflow: hidden;
            flex-wrap: wrap;
            justify-content: center;
        }

        @include lg {
            justify-content: center;
            flex-wrap: wrap;
            row-gap: 24px;
            column-gap: 40px;
            overflow-x: initial;

            &--fade {
                mask-image: none;
                -webkit-mask-image: none;
            }
        }
    }
}
</style>
